import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Modal from '../layouts/Modal';
import Button from '../misc/Button';
import ToggleSwitch from '../misc/ToggleSwitch';
import ParticipantPicker from '../misc/ParticipantPicker';
import { ScoreCard, updateScoreCard, getAllScoreCards } from '../../services/scoreCardService';
import { getScoreCardTypeLabelKey } from '../../utils/scoreCardUtil';

interface EditScoreCardModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  card: ScoreCard | null;
  onSave?: (updated: ScoreCard) => void;
}

export default function EditScoreCardModal({ open, setOpen, card, onSave }: EditScoreCardModalProps) {
  const { t } = useTranslation('', { keyPrefix: 'page.community.scores.forms' });
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [memberEditAccess, setMemberEditAccess] = useState(false);
  const [containsAllMembers, setContainsAllMembers] = useState(true);
  const [selectedMembers, setSelectedMembers] = useState<number[]>([]);
  const [ascending, setAscending] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (!card) return;
    setName(card.name);
    setDescription(card.description ?? '');
    setMemberEditAccess(Boolean(card.memberEditAccess));
    setContainsAllMembers(Boolean(card.containsAllMembers));
    setSelectedMembers(card.selectedUserIds || []);
    setAscending(Boolean(card.ascending));
  }, [open, card]);

  const handleSave = async () => {
    if (!card) return;
    if (!name.trim()) {
      toast.error(t('errors.nameRequired'));
      return;
    }

    setLoading(true);
    try {
      await updateScoreCard(card.communityId, card.scoreCardId, {
        name: name.trim(),
        description,
        memberEditAccess,
        containsAllMembers,
        ascending,
      });

      // Re-fetch to get the updated card (server returns no body for update)
      const refreshed = await getAllScoreCards(card.communityId);
      const updatedCard = refreshed.find((c) => c.scoreCardId === card.scoreCardId);
      if (updatedCard) {
        toast.success(t('saveSuccess') || 'Saved');
        onSave?.(updatedCard);
        setOpen(false);
      } else {
        toast.error(t('errors.updateFailed'));
      }
    } catch (err: any) {
      console.error('Failed updating score card', err);
      toast.error(t('errors.updateFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={open} setOpen={setOpen} title={<>{t('editTitle') || t('createTitle')}</>}>
      <div className="flex flex-col gap-4 pt-2">
        <div>
          <label className="mb-1 block text-sm font-medium text-primary">{t('name')} </label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full rounded-lg border-none !bg-secondary-50 p-2"
          />
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-primary">{t('description')}</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t('descriptionPlaceholder')}
            rows={3}
            className="w-full rounded-lg border-none !bg-secondary-50 p-2"
          />
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-primary">{t('typeLabel')}</label>
          <div className="w-full rounded-lg bg-secondary-50 p-2 text-details/80">
            {card ? t(`type.${getScoreCardTypeLabelKey(card.type)}`) : '-'}
          </div>
        </div>

        <div>
          <p className="font-medium">{t('membersCanEdit')}</p>
          <div className="mt-2">
            <ToggleSwitch
              leftLabel={t('no')}
              rightLabel={t('yes')}
              value={memberEditAccess}
              onChange={(v) => setMemberEditAccess(v)}
            />
          </div>
        </div>

        <div>
          <p className="font-medium">{t('includeAllMembers')}</p>
          <div className="mt-2">
            <ToggleSwitch
              leftLabel={t('no')}
              rightLabel={t('yes')}
              value={containsAllMembers}
              onChange={(v) => setContainsAllMembers(v)}
            />
          </div>
        </div>

        <div>
          <p className="font-medium">{t('ascending')}</p>
          <div className="mt-2">
            <ToggleSwitch
              leftLabel={t('highest')}
              rightLabel={t('lowest')}
              value={ascending}
              onChange={(v) => setAscending(v)}
            />
          </div>
        </div>

        {!containsAllMembers && (
          <div>
            <p className="mb-2 font-medium">{t('selectMembers')}</p>
            <ParticipantPicker
              communityId={card?.communityId}
              selected={selectedMembers}
              setSelected={setSelectedMembers}
            />
          </div>
        )}

        <div className="mt-4 flex justify-end gap-3">
          <Button variant="secondary" onClick={() => setOpen(false)}>
            {t('cancel')}
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={loading}>
            {loading ? t('saving') : t('save')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
